export type EarTrainingInstrument =
  | 'triangle'
  | 'piano'
  | 'flute'
  | 'guitar'
  | 'clarinet'
  | 'organ';

const STORAGE_KEY = 'earTraining.prefs';

export type EarTrainingPreferences = {
  instrument: EarTrainingInstrument;
  showKeyLetter: boolean;
  showStartingNote: boolean;
  /** Settings panel expanded (default collapsed to save space). */
  settingsExpanded: boolean;
  /** Max melody difficulty rank (1–10); only pieces at or below this level are picked. */
  maxDifficulty: number;
};

const DEFAULTS: EarTrainingPreferences = {
  instrument: 'piano',
  showKeyLetter: false,
  showStartingNote: false,
  settingsExpanded: false,
  maxDifficulty: 10,
};

export function loadEarTrainingPreferences(): EarTrainingPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<EarTrainingPreferences>;
    return {
      instrument: isInstrument(parsed.instrument) ? parsed.instrument : DEFAULTS.instrument,
      showKeyLetter: !!parsed.showKeyLetter,
      showStartingNote: !!parsed.showStartingNote,
      settingsExpanded: !!parsed.settingsExpanded,
      maxDifficulty: clampDifficulty(parsed.maxDifficulty ?? DEFAULTS.maxDifficulty),
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveEarTrainingPreferences(prefs: EarTrainingPreferences): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

function clampDifficulty(n: number): number {
  if (!Number.isFinite(n)) return DEFAULTS.maxDifficulty;
  return Math.max(1, Math.min(10, Math.round(n)));
}

function isInstrument(v: unknown): v is EarTrainingInstrument {
  return (
    v === 'triangle' ||
    v === 'piano' ||
    v === 'flute' ||
    v === 'guitar' ||
    v === 'clarinet' ||
    v === 'organ'
  );
}

export const INSTRUMENT_LABELS: Record<EarTrainingInstrument, string> = {
  triangle: 'Soft synth',
  piano: 'Piano',
  flute: 'Flute',
  guitar: 'Guitar',
  clarinet: 'Clarinet',
  organ: 'Organ',
};
